import { Input } from './input.mjs';
import { resolveAdminUrl, registerCustomElement } from '../utils.mjs';
import RemoteForm from '../controls/remoteFormPanel.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { create } from '../utils.mjs';
import EVENTS from '../events.mjs';
import FormanConfig from '../formanConfig.mjs';
import { ScenarioListItem } from './scenario-list-item.mjs';
import Features from '../features.mjs';
import classes from './scenario-list.module.scss';

import common from '../common.module.scss';
import Loader from '../loader.mjs';
import { getModuleData, getModuleConfigForm, getModuleConfigFormValue } from './scenario-list-tools.mjs';
import { getLetAIDecideInputConfig } from '../ai/let-ai-decide/let-ai-decide.ts';
import { openAppSearch } from '../app-search/app-search.ts';

const config = FormanConfig.config.fields.scenariolist;
const APPROVAL_MODE_FF = 'ai-agent-approval-mode';

export class ScenarioListInput extends Input {
	constructor(...args) {
		super(...args);

		this.value = [];
		this.scenariosCache = [];
	}

	get allowApiCall() {
		return false;
	}

	/**
	 * This is supposed to be overridden by the AiAgentToolInput class.
	 *
	 * @param {Array<{ id: number }>} _updatedScenarios - updated input value
	 * @param {'added' | 'removed'} _typeOfChange - If the scenario was added or removed
	 * @param {{ id: number; name: string }} _scenarioChanged - The scenario that was added or removed
	 *
	 * @returns {Promise<boolean>} - Returns true/undefined if the scenario was successfully patched or false if it was not changed
	 */
	async patchScenarioList(_updatedScenarios, _typeOfChange, _scenarioChanged) {
		return true;
	}

	getCurrentScenarios() {
		const scenarios = this.value;

		if (this.scenariosCache) {
			return this.refreshScenarios(this.scenariosCache, scenarios);
		}

		return scenarios;
	}

	async deleteScenario(id) {
		const scenario = this.getCurrentScenarios().find((item) => item.id === id);

		if (!scenario) {
			console.warn('Attempted to remove scenario that does not exist');
			return;
		}

		const values = this.value.filter((item) => item.id !== id);

		if ((await this.patchScenarioList(values, 'removed', scenario)) === false) {
			return;
		}

		// Update input value
		this.value = values;
		this.onChange();

		// Update DOM
		const scenarioElements = Array.from(this.querySelectorAll('imt-scenario-list-item'));
		const deletedElement = scenarioElements.find((item) => item.getAttribute('data-id') === String(id));
		deletedElement?.remove();
	}

	async addScenario(scenario, approvalMode) {
		const alreadyHasScenario = this.value.some(({ id }) => id === scenario.id);

		if (alreadyHasScenario) {
			console.warn('Attempted to add scenario that already exists in list');
			return;
		}

		const alreadyCached = this.scenariosCache.some(({ id }) => id === scenario.id);
		if (!alreadyCached) {
			this.scenariosCache.push(scenario);
		}

		const addValue = { id: scenario.id };

		if (Features.isOn(APPROVAL_MODE_FF)) {
			addValue.approvalMode = approvalMode;
		}

		const values = this.value.concat(addValue);

		if ((await this.patchScenarioList(values, 'added', scenario)) === false) {
			return;
		}

		// Update input value
		this.value = values;
		this.onChange();

		// Update DOM
		if (scenario.type === 'tool') {
			this.toolList.append(this.createScenarioElement(scenario));
		} else {
			this.scenarioList.append(this.createScenarioElement(scenario));
		}
	}

	updateScenario(id, data) {
		const currentScenarios = this.getCurrentScenarios();
		const updateScenario = currentScenarios.find((scenario) => id === scenario.id);

		if (updateScenario) {
			Object.assign(updateScenario, data);
		}
	}

	async openUpdateScenarioPanel(id, target) {
		if (this.panel) return;

		this.panel = new RemoteForm(target);

		this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
			this.disabled = false;
			this.panel = null;
		});

		const scenario = this.getCurrentScenarios().find((scenario) => scenario.id === id);
		const approvalMode = this.value?.find((x) => x.id === id)?.approvalMode;

		const updateScenarioConfig = [
			{
				name: 'description',
				label: I18n.l('scenarioList.description'),
				type: 'text',
				multiline: true,
				required: true,
				validate: {
					max: 240,
				},
				default: scenario.description,
			},
		];

		if (Features.isOn(APPROVAL_MODE_FF)) {
			updateScenarioConfig.push(this.getApprovalModeConfig(approvalMode));
		}

		await this.panel.build(
			{
				name: I18n.l('scenarioList.updatePanelName'),
				config: updateScenarioConfig,
				theme: this._instructions?.theme,
				buttons: [
					{
						label: I18n.l('common.close'),
						action: 'close',
					},
					{
						label: I18n.l('panels.rpc.ok'),
						action: 'handler',
						handler: async (e) => {
							if (!this.panel.form.validate()) {
								return;
							}

							const { values } = this.panel.form;

							if (values.changed) {
								const { description, approvalMode } = this.panel.form.value;

								if (Features.isOn(APPROVAL_MODE_FF)) {
									this.value.find((x) => x.id === id).approvalMode = approvalMode;
								}

								await this.patchScenario(scenario.id, { description });
							}

							this.panel.panel.close();
						},
					},
				],
			},
			{},
		);

		this.panel.open();
	}

	createScenarioElement(
		{ id, name, usedPackages: packages, isActive, type, module },
		{ readonly = false } = { readonly: false },
	) {
		const teamId = this.form?.meta?.teamId;

		return ScenarioListItem.create({
			id,
			name,
			disabled: !isActive,
			packages: this.getScenarioListItemPackages(type, module, packages),
			href: teamId ? resolveAdminUrl(`${teamId}/scenarios/${id}/edit`) : '',
			readonly,
			type,
			repository: this.form.meta.repository,
			onDelete: () => {
				this.form.tracking?.trackScenarioListAction({
					entityType: type === 'tool' ? 'tool' : 'scenario',
					actionType: 'delete',
				});
				this.deleteScenario(id);
			},
			onConfig: async (ev) => {
				this.form.tracking?.trackScenarioListAction({
					entityType: type === 'tool' ? 'tool' : 'scenario',
					actionType: 'edit',
				});
				if (type === 'tool') {
					this._editToolHandler(id, ev.target);
				} else {
					this.openUpdateScenarioPanel(id, ev.target);
				}
			},
		});
	}

	async loadScenarios(instructions, type = 'all') {
		const { scenarios } = await Loader.guardedLoad(
			this.getUrlWithScenarioType(instructions?.options?.store || config.scenarios.list.path, type),
			{
				context: this.form.meta.imlContext,
			},
			this.allowApiCall,
		);

		return scenarios;
	}

	/*
	 * The data that we get from the value might not be valid and we need to check the most recent scenario data instead
	 */
	refreshScenarios(scenarios, value = []) {
		return (
			value?.flatMap(({ id }) => {
				const currentScenario = scenarios.find((scenario) => scenario.id === id);

				if (currentScenario) {
					return currentScenario;
				}

				console.warn(`[ScenarioListInput] Unable to find scenario with id: ${id}`);

				return [];
			}) || []
		);
	}

	async patchScenario(id, scenario) {
		await Loader.guardedLoad(
			config.scenarios.patch.path,
			{
				method: 'PATCH',
				body: JSON.stringify(scenario),
				context: {
					scenarioId: id,
				},
			},
			true,
		);

		this.updateScenario(id, scenario);
	}

	async _build(instructions, value, metadata) {
		const description = create(`div.${classes.sectionDescription}`);
		description.textContent = instructions.listDescription ?? I18n.l('scenarioList.listDescription');

		this.appendChild(description);

		if (instructions.scenarios) {
			this.scenariosCache = instructions.scenarios;
		} else {
			const scenariosResponse = await this.loadScenarios(instructions);

			// for backward compatibility
			const grouped = scenariosResponse.some((x) => Array.isArray(x.options));
			if (grouped) {
				this.scenariosCache = scenariosResponse.reduce((result, group) => {
					return result.concat(group.options);
				}, []);
			} else {
				this.scenariosCache = scenariosResponse;
			}
		}

		const scenarios = this.refreshScenarios(this.scenariosCache, value);

		const scenarioElements = [];
		const toolElements = [];

		scenarios.forEach((scenario) => {
			if (scenario.type === 'tool') {
				toolElements.push(this.createScenarioElement(scenario, { readonly: instructions.readonly }));
			} else {
				scenarioElements.push(this.createScenarioElement(scenario, { readonly: instructions.readonly }));
			}
		});

		this.toolList = create(`div.${classes.scenarioList}`);
		this.toolList.append(create(`div ${I18n.l('scenarioList.modules')}`));

		this.toolList.append(...toolElements);
		this.append(this.toolList);

		this.scenarioList = create(`div.${classes.scenarioList}`);
		this.scenarioList.append(create(`div ${I18n.l('scenarioList.scenarios')}`));

		this.scenarioList.append(...scenarioElements);
		this.append(this.scenarioList);

		this.value = value?.filter((x) => scenarios?.some((y) => x.id === y.id)) || [];

		if (!instructions.readonly) {
			if (!Features.isOn('modules-as-tools')) {
				const addScenarioBtn = create(
					`button[type="button"].${common.button}.${common.buttonSmall}.${common.buttonGhost}.${classes.addButton}`,
				);

				addScenarioBtn.append(create('i.fa-regular.fa-plus'), I18n.l('scenarioList.addTools'));
				addScenarioBtn.addEventListener('click', async () => {
					await this._addHandler(addScenarioBtn, instructions);
				});

				this.appendChild(addScenarioBtn);
			} else {
				this.showAddScenario = async (target, parentForm) => {
					await this._addHandler(target, instructions, false, parentForm);
				};

				this.showAddModule = async (target, parentForm) => {
					await this._addHandler(target, instructions, true, parentForm);
				};

				let dropdownOpened = false;

				const buttons = create('div.input-group-append');
				const dropdownButton = create(
					`button[type="button"].addButton.${common.button}.${common.buttonSmall}.${common.buttonGhost}.${classes.addButton}`,
				);
				dropdownButton.append(create('i.fa-regular.fa-plus'), I18n.l('scenarioList.addTools'));
				dropdownButton.addEventListener('click', () => {
					this.form.tracking?.trackAiAgentAddTools();
					toggleDropdown();
				});

				const dropdownMenu = create('div.imt-btn-dropdown-menu');
				dropdownButton.addEventListener('focusout', (e) => {
					if (dropdownOpened) {
						toggleDropdown();
					}
				});

				function toggleDropdown() {
					const box = dropdownButton.getBoundingClientRect();

					dropdownOpened = !dropdownOpened;
					dropdownMenu.style.display = dropdownOpened ? 'block' : 'none';

					if (dropdownOpened) {
						const ddMargin = 8;
						const viewportHeight = window.innerHeight;
						const spaceBelow = viewportHeight - (box.bottom + ddMargin);
						const onBottom = spaceBelow > dropdownMenu.offsetHeight;
						const top = onBottom
							? box.y + buttons.clientHeight - ddMargin
							: box.y - (dropdownMenu.offsetHeight + ddMargin);
						dropdownMenu.style.top = `${top}px`;
						dropdownMenu.style.left = `${box.x}px`;

						if (onBottom) {
							dropdownMenu.classList.remove('on-top');
						} else {
							dropdownMenu.classList.add('on-top');
						}
					}
				}

				const addTool = create(`button[type="button"].dropdown-item`);
				addTool.append(create('i.fa-regular.fa-circle.mr-2'), I18n.l('scenarioList.module'));

				addTool.addEventListener('mousedown', async () => {
					this.form.tracking?.trackAiAgentDropdownSelect({
						dropdownItem: 'module',
					});
					await this._addHandler(dropdownButton, instructions, true);
				});
				dropdownMenu.appendChild(addTool);

				const addScenario = create(`button[type="button"].dropdown-item`);
				addScenario.append(create('i.fa-regular.fa-share-alt.mr-2'), I18n.l('scenarioList.scenario'));
				addScenario.addEventListener('mousedown', async () => {
					this.form.tracking?.trackAiAgentDropdownSelect({
						dropdownItem: 'scenario',
					});
					await this._addHandler(dropdownButton, instructions);
				});

				dropdownMenu.appendChild(addScenario);

				buttons.appendChild(dropdownButton);
				buttons.appendChild(dropdownMenu);

				this.append(buttons);
			}
		}
	}

	async _getToolPanel(target, title, actionBtn, parentForm = undefined) {
		const toolPanel = new RemoteForm(target, parentForm);

		toolPanel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
			this.toolPanel = null;
		});

		await toolPanel.build(
			{
				name: title,
				config: [],
				theme: this._instructions?.theme,
				buttons: [
					{
						action: 'close',
						label: I18n.l('common.close'),
					},
					actionBtn,
				],
			},
			{},
			false,
		);

		toolPanel.panel.setLoading(true);
		toolPanel.open();

		return toolPanel;
	}

	_openToolPanelForm(toolPanel, form) {
		if (toolPanel) {
			toolPanel.panel.setLoading(false);
			form.once('has_advanced_parameters', () => {
				const controls = toolPanel.panel.footerControls[0];

				if (controls.querySelector('label.show-advanced-label')) {
					return;
				}

				const advanced = create(`imt-switch.xs`);
				const label = create(`label.show-advanced-label`);

				label.textContent = I18n.l('common.showAdvanced');
				advanced.addEventListener('input', () => (form.advancedParametersVisible = advanced.active));

				controls.appendChild(advanced);
				controls.appendChild(label);
			});

			toolPanel.panel.body.appendChild(form);
		}
	}

	_openToolPanelError(toolPanel, message) {
		toolPanel.panel.setLoading(false);
		toolPanel.panel.warn(new Error(message));
	}

	async _editToolHandler(toolId, target) {
		if (this.toolPanel) {
			return;
		}

		let moduleData;
		let toolData;
		let form;

		this.toolPanel = await this._getToolPanel(target, I18n.l('scenarioList.updateTool'), {
			label: I18n.l('common.save'),
			action: 'handler',
			handler: async (e) => {
				if (form.isValid()) {
					const inputsConfig = getLetAIDecideInputConfig(form);
					const modulevalue = getModuleConfigFormValue(form, moduleData);
					const aiFields = form.toJSON('aiagent');

					try {
						this.toolPanel.panel.setLoading(true);
						const data = await Loader.guardedLoad(
							`api://scenarios/tools/${toolData.id}`,
							{
								method: 'PATCH',
								body: JSON.stringify({
									name: aiFields.name,
									description: aiFields.description,
									inputs: inputsConfig,
									module: modulevalue,
								}),
							},
							true,
						);
						this.toolPanel.panel.setLoading(false);

						const scenario = this.processToolResponse(data);
						this.updateScenario(scenario.id, scenario);
						const scenarioListItem = this.querySelector(`imt-scenario-list-item[data-id="${scenario.id}"]`);
						if (scenarioListItem) {
							if (scenario.name) {
								scenarioListItem.setAttribute('name', scenario.name);
							}
						}

						this.toolPanel.panel.close();
					} catch (error) {
						this.toolPanel.panel.setLoading(false);
						console.error('Error adding tool:', error);
						this._openToolPanelError(this.toolPanel, I18n.l('scenarioList.updateToolError'));
					}
				}
			},
		});

		try {
			const res = await Loader.guardedLoad(
				`api://scenarios/tools/${toolId}`,
				{
					method: 'GET',
				},
				true,
			);

			toolData = this.processToolResponse(res);
			const [appName, moduleName] = toolData.module.module.split(':');

			moduleData = await getModuleData(appName, moduleName, toolData.module.version);
		} catch {
			this._openToolPanelError(this.toolPanel, I18n.l('scenarioList.loadToolError'));
			this.toolPanel.panel.footer.hidden = true;
			return;
		}

		form = getModuleConfigForm({
			moduleData,
			config: toolData.module,
			toolConfig: {
				name: toolData.name,
				description: toolData.description,
			},
			inputs: toolData.inputs,
			options: this.form.options,
		});
		this._openToolPanelForm(this.toolPanel, form);
	}

	async _addToolHandler(target, parentForm) {
		const selectedModule = await openAppSearch(target, parentForm);

		if (!selectedModule) {
			return;
		}

		if (this.toolPanel) return;
		let moduleData;
		let form;

		this.toolPanel = await this._getToolPanel(
			target,
			I18n.l('scenarioList.addModuleTool'),
			{
				label: I18n.l('scenarioList.add'),
				action: 'handler',
				handler: async (e) => {
					const form = this.toolPanel.panel.querySelector('.i-formula.formula');

					if (form.isValid()) {
						const inputsConfig = getLetAIDecideInputConfig(form);
						const modulevalue = getModuleConfigFormValue(form, moduleData);
						const aiFields = form.toJSON('aiagent');

						try {
							this.toolPanel.panel.setLoading(true);
							const data = await Loader.guardedLoad(
								`api://scenarios/tools`,
								{
									method: 'POST',
									body: JSON.stringify({
										name: aiFields.name,
										teamId: this.form.meta.teamId,
										description: aiFields.description,
										inputs: inputsConfig,
										module: modulevalue,
									}),
								},
								true,
							);
							this.toolPanel.panel.setLoading(false);

							const scenario = this.processToolResponse(data);
							this.addScenario(scenario);

							this.toolPanel.panel.close();
						} catch (error) {
							this.toolPanel.panel.setLoading(false);
							console.error('Error adding tool:', error);
							const errorMessage =
								error.code === 'IM324'
									? I18n.l('scenarioList.maxScenariosActive')
									: I18n.l('scenarioList.addToolError');
							this._openToolPanelError(this.toolPanel, errorMessage);
						}
					}
				},
			},
			parentForm,
		);

		try {
			moduleData = await getModuleData(selectedModule.app.name, selectedModule.module.name, selectedModule.version);
		} catch {
			this._openToolPanelError(this.toolPanel, I18n.l('scenarioList.loadToolError'));
			this.toolPanel.panel.footer.hidden = true;
			return;
		}

		form = getModuleConfigForm({ moduleData, options: this.form.options });
		this._openToolPanelForm(this.toolPanel, form);
	}

	async _addHandler(target, instructions, addTool = false, parentForm = null) {
		if (this.panel) return;

		this.disabled = true;

		this.panel = new RemoteForm(target, parentForm);

		this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
			this.disabled = false;
			this.panel = null;
		});

		const metadata = {};

		const instructionsQuery = new URLSearchParams();
		instructionsQuery.append('teamId', instructions.options?.team || this.form.meta.teamId);
		instructionsQuery.append('pg[limit]', config.scenarios.list.limit || 5000);

		const ignoredScenarios = this.getCurrentScenarios().map(({ id }) => id);

		const onAddToolEventName = 'onAddTool';

		let store = instructions?.options?.store || config.scenarios.list.path;
		if (addTool) {
			store = this.getUrlWithScenarioType(store, 'tool');
		}

		const addScenarioConfig = [
			{
				name: 'scenarioId',
				label: addTool ? I18n.l('scenarioList.module') : I18n.l('scenarioList.scenarios'),
				placeholder: addTool ? I18n.l('scenarioList.selectModule') : I18n.l('scenarioList.selectScenario'),
				searcherPlaceholder: addTool ? I18n.l('scenarioList.searchModuleToolPlaceholder') : undefined,
				type: 'scenario',
				required: true,
				grouped: true,
				showValue: false,
				showPackages: true,
				showActiveBadge: !addTool,
				scenarioType: addTool ? 'tool' : 'scenario',
				allowCreateOption: Features.isOn('ai-agent-create-scenario') || addTool,
				options: {
					store,
					ignore: ignoredScenarios,
					label: 'name',
					value: 'id',
				},
				nested: [
					{
						name: 'description',
						label: I18n.l('scenarioList.description'),
						type: 'text',
						multiline: true,
						required: true,
						validate: {
							max: 240,
						},
					},
				],
			},
		];

		if (Features.isOn(APPROVAL_MODE_FF)) {
			addScenarioConfig[0].nested.push(this.getApprovalModeConfig('auto-run'));
		}

		if (addTool) {
			addScenarioConfig[0].createOptionAction = {
				label: I18n.l('scenarioList.createTool'),
				event: onAddToolEventName,
			};
		} else {
			addScenarioConfig.push({
				type: 'banner',
				theme: 'warning',
				text: I18n.l('scenarioList.onlyOnDemand'),
			});
		}

		this.extendAddScenarioConfig(addScenarioConfig);

		this.panel
			.build(
				{
					name: addTool ? I18n.l('scenarioList.addModuleTool') : I18n.l('scenarioList.addTools'),
					config: addScenarioConfig,
					buttons: [
						{
							action: 'close',
							label: I18n.l('common.close'),
						},
						{
							label: I18n.l('scenarioList.add'),
							action: 'handler',
							handler: async (e) => {
								this.form.tracking?.trackModuleAdd();

								if (!this.panel.form.validate()) return;

								const scenarioId = this.panel?.panel?.querySelector('imt-input-scenario > imt-picker')?.value;
								const scenario = this.getScenarioData(scenarioId);

								if (!scenario) {
									this.panel.panel.close();
									return;
								}

								const id =
									isNaN(scenario.id) && scenario?.id.startsWith('SCN_') ? +scenario.id.substring(4) : scenario.id;

								await this.addScenario({ ...scenario, id }, this.panel.form?.value?.approvalMode);

								const { description } = this.panel.form.value;
								const descriptionChanged = description != undefined && description !== scenario.description;

								if (descriptionChanged) {
									await this.patchScenario(id, { description });
								}

								this.panel.panel.close();
							},
						},
					],
					theme: instructions?.theme,
				},
				metadata,
			)
			.then(() => {
				this.panel.panel.setLoading(false);
			});

		this.panel.panel.setLoading(true);
		this.panel.open();

		this.panel.form.addEventListener(onAddToolEventName, () => {
			this.panel.panel.close();
			this.panel = null;

			this._addToolHandler(target, parentForm);
		});

		// When the nested field changes, find the only visible nested fieldset and update its description
		this.panel.form.addEventListener(EVENTS.INPUT.NESTED_CHANGED, (event) => {
			const descriptionTextArea = event.target.nestedFieldset?.querySelector('textarea[name=description]');

			if (!descriptionTextArea) {
				return;
			}

			const scenarioId = event.target.querySelector('imt-picker')?.value;
			const scenario = this.getScenarioData(scenarioId);

			descriptionTextArea.value = scenario?.description || '';
		});
	}

	getApprovalModeConfig(defaultValue) {
		return {
			name: 'approvalMode',
			type: 'select',
			options: [
				{
					label: I18n.l('scenarioList.approvalModeAuto'),
					value: 'auto-run',
					icon: 'bolt',
				},
				{
					label: I18n.l('scenarioList.approvalModeRequired'),
					value: 'approval-required',
					icon: 'octagon-exclamation',
				},
			],
			label: I18n.l('scenarioList.approvalModeLabel'),
			help: I18n.l('scenarioList.approvalModeHelp'),
			default: defaultValue,
			required: true,
		};
	}

	getScenarioData(scenarioId) {
		return (
			this.scenariosCache.find(({ id }) => scenarioId === id) ||
			this.panel?.panel?.querySelector('imt-input-scenario > imt-picker')?.getOptionData()
		);
	}

	getUrlWithScenarioType(url, scenarioType) {
		if (!url || !scenarioType) {
			return url;
		}

		const typeQueryParam = new URLSearchParams();
		typeQueryParam.append('type', scenarioType);

		if (url.includes('?')) {
			return `${url}&${typeQueryParam.toString()}`;
		} else {
			return `${url}?${typeQueryParam.toString()}`;
		}
	}

	/**
	 * Extends the add scenario panel configuration.
	 * @param {Array<object>} addScenarioConfig - The configuration array for the add panel.
	 */
	extendAddScenarioConfig(addScenarioConfig) {}

	getScenarioListItemPackages(type, module, packages) {
		if (type === 'tool') {
			const moduleToolScenarioPackage = module?.module?.split(':')[0];

			if (moduleToolScenarioPackage) {
				return [moduleToolScenarioPackage];
			}

			if (packages?.length > 1) {
				return [packages[1]];
			}
		}

		return packages;
	}

	processToolResponse(response) {
		// Backward compatibility for POST/PATCH tool endpoints
		if (response.scenario) {
			return response.scenario;
		}

		// New/Unified Module Tool API
		if (response.tool) {
			response.tool.type = 'tool';
			return response.tool;
		}

		// Backward compatibility for GET tool endpoint
		return response;
	}
}

registerCustomElement('imt-input-scenariolist', ScenarioListInput);
